<template>
  <div class="case" :data-comp="blok.component">
    <l-section>
      <l-container>
        <l-flex class="case__hero">
          <a-header :displayFont="true">{{ title }}</a-header>
          <div class="case__hero-meta" v-if="blok.client && dateString">
            <span v-if="blok.client">{{ blok.client }}</span>
            <span v-if="blok.client && dateString"> - </span>
            <span v-if="dateString">{{ dateString }}</span>
          </div>
          <hr class="case__hero-hr" v-if="hasConcept" />
          <div class="case__hero-concept" v-if="hasConcept">
            <sb-richtext :text="blok.concept" />
          </div>
        </l-flex>
        <div></div>
      </l-container>
    </l-section>
    <l-section>
      <div class="case__media-container">
        <component
          v-for="media in blok.media"
          :key="media._uid"
          :is="media.component"
          :blok="media"
          v-editable="media"
        />
      </div>
    </l-section>
    <o-cases-related :fullSlug="fullSlug" :type="blok.type" />
  </div>
</template>

<script setup>
import { onActivated, onMounted, computed, watchEffect, onBeforeUnmount, onDeactivated } from "vue";
import { useThemeColor } from "@/composables/useThemeColor.js";
import { useIndexedDb } from "@/composables/useIndexedDb.js";
import { useNow } from "@vueuse/core";
import { useRoute } from "vue-router";

const { themeBgColor, themeTextColor } = useThemeColor();
const { dbEntry } = useIndexedDb();
const route = useRoute();
const now = useNow();

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  blok: {
    type: Object,
    default: () => ({}),
  },
});

// remove leading slash and trailing slash
const fullSlug = computed(() => route.fullPath.replace(/^\//, "").replace(/\/$/, ""));

const dbEntryCase = await dbEntry("cases", fullSlug.value, {});

onMounted(() => {
  dbEntryCase.value.last_visited = now.value;
  setTheme();
});
onActivated(() => {
  dbEntryCase.value.last_visited = now.value;
  setTheme();
});

onDeactivated(() => (dbEntryCase.value.last_visited = now.value));
onBeforeUnmount(() => (dbEntryCase.value.last_visited = now.value));

const setTheme = () => {
  themeBgColor.value = props.blok.background_color.color;
  themeTextColor.value = props.blok.text_color.color;
};

watchEffect(() => {
  // make color update automatically in the Storyblok editor
  themeBgColor.value = props.blok.background_color.color;
  themeTextColor.value = props.blok.text_color.color;
});

const hasConcept = computed(() => {
  return (
    props.blok.concept &&
    props.blok.concept.content &&
    props.blok.concept.content.length &&
    props.blok.concept.content[0].content
  );
});

const dateString = computed(() => {
  if (!props.blok.date) return "";
  let date = new Date(props.blok.date);
  date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${month}, ${year}`;
});
</script>

<style lang="scss" scoped>
.case {
  --bg-color: v-bind(themeBgColor);
  --text-color: v-bind(themeTextColor);
  background-color: var(--bg-color);
  color: var(--text-color);
  &__hero {
    padding-top: global.clampPx(mobile-portrait-small, desktop-large, 60px, 80px);
    text-align: center;
  }
  &__hero-meta {
    font-size: var(--font-size-sm);
    line-height: var(--line-height-sm);
    font-weight: var(--font-weight-book);
  }
  &__hero-concept {
    max-width: 45rem;
    margin: 0 auto;
  }
  &__hero-hr {
    width: 1rem;
    height: 0.2rem;
    border-radius: 4rem;
    margin: global.clampPx(mobile-portrait-small, desktop-large, 0px, 15px) auto
      global.clampPx(mobile-portrait-small, desktop-large, 0px, 15px);
    background-color: var(--text-color);
    opacity: 0.2;
    border: none;
  }
  &__media-container {
    padding: 0 0 10rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: global.clampPx(mobile-portrait-small, desktop-large, 100px, 200px) 0;
  }
}
</style>
